import { queryDashboard } from '../../services/api/sdk.gen';
import type {
  DashboardQueryName,
  DashboardQueryRequest,
  DashboardQueryResponse,
  DashboardWidgetOptions,
} from '../../services/api/types.gen';
import type {
  WidgetItemLimitPreferences,
  WidgetViewPreferences,
} from './useWidgetDisplayPreferences';

export interface DashboardRequestInput {
  queries: DashboardQueryName[];
  clinicIds: string[];
  viewPreferences: WidgetViewPreferences;
  itemLimits: WidgetItemLimitPreferences;
  include_imported_patients?: boolean;
  include_test_patients?: boolean;
  signal?: AbortSignal;
}

// Dashboard cards should fail fast enough that clinicians can retry instead of
// waiting on a stuck aggregate request.
const DASHBOARD_REQUEST_TIMEOUT_MS = 12000;

export function buildDashboardWidgetOptions(
  viewPreferences: WidgetViewPreferences,
  itemLimits: WidgetItemLimitPreferences
): DashboardWidgetOptions {
  const insurerView = viewPreferences.census_by_primary_insurer;
  const diagnosisView = viewPreferences.census_by_primary_diagnosis;

  // Grouped census widgets need the active view's limit on the server so the
  // "Other" bucket matches what the clinician is looking at.
  return {
    case_manager_queue: { view: viewPreferences.case_manager_queue },
    past_due_tasks: { view: viewPreferences.past_due_tasks },
    census_by_primary_insurer: {
      view: insurerView,
      limit: itemLimits.census_by_primary_insurer[insurerView],
    },
    census_by_primary_diagnosis: {
      view: diagnosisView,
      limit: itemLimits.census_by_primary_diagnosis[diagnosisView],
    },
  };
}

export function buildDashboardRequest({
  queries,
  clinicIds,
  viewPreferences,
  itemLimits,
  include_imported_patients = false,
  include_test_patients = false,
}: DashboardRequestInput): DashboardQueryRequest {
  return {
    queries,
    // Empty means "All Clinics" to the API; the frontend-only sentinel should
    // never be sent over the wire.
    clinic_ids: [...new Set(clinicIds)].sort(),
    widget_options: buildDashboardWidgetOptions(viewPreferences, itemLimits),
    include_imported_patients,
    include_test_patients,
  };
}

export async function fetchDashboard(
  input: DashboardRequestInput
): Promise<DashboardQueryResponse> {
  const response = await queryDashboard({
    body: buildDashboardRequest(input),
    signal: input.signal,
    throwOnError: true,
    timeout: DASHBOARD_REQUEST_TIMEOUT_MS,
  });
  return response.data;
}
